import { Suspense, lazy, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AlbumsIcon, ChatIcon, PhotosIcon, SettingsIcon, UploadIcon } from './components/Icons';
import { Onboarding, shouldShowOnboarding } from './components/Onboarding';
import { AlbumPage } from './pages/AlbumPage';
import { AlbumsPage } from './pages/AlbumsPage';
import { FeedPage } from './pages/FeedPage';
import { DropPage } from './pages/DropPage';
import { DropsPage } from './pages/DropsPage';
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { StoriesPage } from './pages/StoriesPage';
import { ExternalDropConversationPage } from './pages/ExternalDropConversationPage';
import './styles/external-drop-chat.css';

// Lazy so the photo feed — the screen people actually open — is not held up by
// code for the other tabs.
const ChatPage = lazy(() =>
  import('./pages/ChatPage').then((module) => ({ default: module.ChatPage })),
);

const InvitePage = lazy(() =>
  import('./pages/InvitePage').then((module) => ({ default: module.InvitePage })),
);

const CirclesPage = lazy(() =>
  import('./pages/CirclesPage').then((module) => ({ default: module.CirclesPage })),
);

const JoinPage = lazy(() =>
  import('./pages/JoinPage').then((module) => ({ default: module.JoinPage })),
);

export function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isChatThread = /^\/chat\/[^/]+/.test(location.pathname);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return localStorage.getItem('family.theme') === 'dark' ? 'dark' : 'light';
  });

  // Evaluated once per mount rather than on every render, so dismissing the
  // flow does not have it immediately re-evaluate and reappear.
  const [showOnboarding, setShowOnboarding] = useState(false);

  /**
   * Run first-time setup as soon as someone signs in. Installing the app and
   * enabling notifications are the two steps people otherwise never get round
   * to, and a notification-less photo app is the thing we are replacing.
   */
  useEffect(() => {
    if (user) setShowOnboarding(shouldShowOnboarding());
  }, [user]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem('family.theme', theme);

    const themeColor = document.querySelector<HTMLMetaElement>('#app-theme-color');
    if (themeColor) themeColor.content = theme === 'dark' ? '#0d110f' : '#1f7a52';

    const colorScheme = document.querySelector<HTMLMetaElement>('#app-color-scheme');
    if (colorScheme) colorScheme.content = theme;
  }, [theme]);

  /**
   * Route to the right screen when a notification is tapped.
   *
   * The service worker cannot navigate an existing client on iOS, so it posts
   * the destination here instead and we route in-app. Without this, tapping a
   * notification would merely focus whatever screen was already open — which
   * reads as the notification being broken.
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'navigate' && typeof event.data.url === 'string') {
        const url = new URL(event.data.url, window.location.origin);
        if (url.origin !== window.location.origin) {
          window.location.assign(url.href);
          return;
        }
        navigate(url.pathname + url.search);
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  if (location.pathname === '/join') {
    return <Suspense fallback={<div className="center-fill"><div className="spinner" role="status" aria-label="Loading invitation" /></div>}><JoinPage /></Suspense>;
  }

  if (loading) {
    return (
      <div className="center-fill">
        <div className="spinner" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  if (showOnboarding) {
    return <Onboarding onClose={() => setShowOnboarding(false)} />;
  }

  const openUpload = () => {
    navigate('/');
    window.setTimeout(() => window.dispatchEvent(new Event('family:open-upload')), 0);
  };

  return (
    <div className="app-shell">
      <main className={`app-main ${isChatThread ? 'chat-thread-main' : ''}`}>
        <Suspense
          fallback={
            <div className="center-fill">
              <div className="spinner" role="status" aria-label="Loading" />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<FeedPage />} />
            <Route path="/albums" element={<AlbumsPage />} />
            <Route path="/album/:name" element={<AlbumPage />} />
            <Route path="/drops" element={<DropsPage />} />
            <Route path="/drops/:id" element={<DropPage />} />
            <Route path="/drops/:id/conversation" element={<ExternalDropConversationPage />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/photo/:fileId" element={<FeedPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/chat/:token" element={<ChatPage />} />
            <Route path="/settings" element={<SettingsPage theme={theme} onThemeChange={setTheme} />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/circles" element={<CirclesPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {!isChatThread && <nav className="bottom-nav" aria-label="Main">
        <NavLink to="/" end>
          <PhotosIcon />
          <span>Feed</span>
        </NavLink>
        <NavLink to="/drops">
          <AlbumsIcon />
          <span>Drops</span>
        </NavLink>
        <button type="button" className="upload-nav" onClick={openUpload} aria-label="Share photos">
          <span className="upload-nav-icon"><UploadIcon /></span>
          <span>Share</span>
        </button>
        <NavLink to="/chat">
          <ChatIcon />
          <span>Chat</span>
        </NavLink>
        <NavLink to="/settings">
          <SettingsIcon />
          <span>Settings</span>
        </NavLink>
      </nav>}
    </div>
  );
}
